/**
 * AuthContext — global auth/session provider for the React app.
 *
 * Exposes `useAuth()` returning:
 *
 *   { user, loading, isAuthenticated, isSuperAdmin,
 *     login, register, logout,
 *     loginWithGoogle, loginWithMicrosoft,
 *     processGoogleSession, processMicrosoftSession,
 *     verify2FA, setup2FA, verify2FASetup, disable2FA,
 *     isImpersonating, originalAdmin, startImpersonation, stopImpersonation,
 *     checkAuth, token, setUser }
 *
 * Auth strategies (priority order):
 *   1. JWT (email + password / refresh) — `access_token` + `refresh_token` in
 *      sessionStorage. 2FA gates a second exchange via `/auth/2fa/verify/`.
 *   2. Emergent-managed Google login (redirect flow). Returns a `session_token`
 *      cookie + sessionStorage entry; backend resolves Google identity → user.
 *   3. Microsoft Entra OAuth — uses `state` for CSRF in sessionStorage.
 *
 * Token storage — security note:
 *   Tokens are stored in sessionStorage rather than localStorage to reduce the
 *   XSS attack surface (tokens do not persist across browser tabs or sessions).
 *   The correct long-term fix is to have the backend set tokens as HttpOnly
 *   cookies (inaccessible to JavaScript entirely); sessionStorage is a
 *   client-side mitigation only and does NOT protect against XSS in the same
 *   tab. See: https://cheatsheetseries.owasp.org/cheatsheets/HTML5_Security_Cheat_Sheet.html
 *
 * Impersonation:
 *   Super admins can POST `/auth/impersonate/start/` to assume another user's
 *   identity. State is mirrored to sessionStorage (`impersonation_data`) so a
 *   page refresh keeps the impersonation active. The DashboardLayout shows a
 *   purple banner whenever `isImpersonating` is true. `stopImpersonation()`
 *   detects tenant-vs-user impersonation and routes to the matching backend
 *   endpoint to restore the original admin's tokens.
 *
 * Side-effects to be aware of:
 *   - `loginWithGoogle()` does a full-page navigation — do NOT call from a
 *     component that needs to keep state across the redirect.
 *   - On token failure (`/auth/me/` 401) we clear ALL auth keys to avoid
 *     half-authenticated states.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const AuthContext = createContext(null);

function resumeOfflinePunches(userData) {
  const userId = userData?.id;
  if (!userId) return;
  try {
    if (sessionStorage.getItem('impersonation_data')) return;
  } catch (error) {
    return;
  }
  import('../lib/offlinePunchQueue')
    .then(({ syncPunchesForUser }) => syncPunchesForUser(api, userId, { punchPhotoOn: false }))
    .catch(() => {});
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalAdmin, setOriginalAdmin] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      let token = sessionStorage.getItem('access_token');
      if (!token) {
        // No access token in this tab (e.g. a fresh tab/window -- sessionStorage
        // doesn't carry over) doesn't mean no session: the backend also sets an
        // httpOnly refresh cookie with a 7-day life specifically so a session
        // survives this. Try it before treating the user as logged out --
        // otherwise that 7-day "stay signed in" is dead on the frontend and
        // every new tab forces a full re-login. Same call shape as the 401
        // refresh path in lib/api.js for consistency.
        try {
          // Send the stored refresh token in the body as well as relying on
          // withCredentials for the httpOnly cookie -- that cookie is
          // cross-site (SPA and API are on different domains) and is dropped
          // by Safari/ITP and third-party-cookie blocking, so cookie-only
          // meant this always failed and every new tab forced a re-login.
          const refreshRes = await api.post('/auth/refresh/', {
            refresh_token: sessionStorage.getItem('refresh_token') || '',
          });
          token = refreshRes.data.access_token;
          sessionStorage.setItem('access_token', token);
          if (refreshRes.data.refresh_token) {
            sessionStorage.setItem('refresh_token', refreshRes.data.refresh_token);
          }
        } catch {
          setLoading(false);
          return;
        }
      }

      const response = await api.get('/auth/me/');
      setUser(response.data);
      resumeOfflinePunches(response.data);

      // Check for impersonation state
      const impersonationData = sessionStorage.getItem('impersonation_data');
      if (impersonationData) {
        const data = JSON.parse(impersonationData);
        setIsImpersonating(true);
        setOriginalAdmin(data.original_admin);
      }
    } catch (error) {
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('impersonation_data');
      setUser(null);
      setIsImpersonating(false);
      setOriginalAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login/', { email, password });

    // Check if 2FA is required
    if (response.data.requires_2fa) {
      return {
        requires_2fa: true,
        tfa_token: response.data.tfa_token,
        tfa_method: response.data.tfa_method || 'totp',
        message: response.data.message
      };
    }

    // The backend sets refresh_token as an httpOnly cookie, which is the
    // PREFERRED path (XSS-protected). It is persisted here as well because
    // that cookie is cross-site (SPA on Vercel, API on Railway/ECS) and is
    // therefore not guaranteed to be stored or replayed by the browser --
    // Safari/ITP and any "block third-party cookies" setting drop it even
    // with SameSite=None. Relying on the cookie alone meant refresh failed
    // 100% of the time in deployed environments and silently logged users
    // out when their access token expired.
    //
    // Trade-off accepted deliberately: a refresh token readable by JS is a
    // real XSS exposure, but a refresh flow that never works is not
    // protecting anything. api.js sends the cookie first and only falls back
    // to this value. Revisit if the API ever moves to the SPA's own origin,
    // at which point the cookie alone is sufficient and this should be
    // removed.
    const { access_token, refresh_token, user: userData } = response.data;

    sessionStorage.setItem('access_token', access_token);
    if (refresh_token) sessionStorage.setItem('refresh_token', refresh_token);
    // Clear any impersonation state on fresh login
    sessionStorage.removeItem('impersonation_data');
    setIsImpersonating(false);
    setOriginalAdmin(null);
    setUser(userData);
    resumeOfflinePunches(userData);

    return userData;
  };

  // Phase 6 — used by GoogleCallback / TwoFactorVerify after server returns a session
  const completeLoginFromResponse = async (data) => {
    if (!data || !data.access_token) return null;
    sessionStorage.setItem('access_token', data.access_token);
    // Persist refresh_token for the same cross-site-cookie reason as login().
    if (data.refresh_token) sessionStorage.setItem('refresh_token', data.refresh_token);
    sessionStorage.removeItem('impersonation_data');
    setIsImpersonating(false);
    setOriginalAdmin(null);
    setUser(data.user);
    resumeOfflinePunches(data.user);
    return data.user;
  };

  const verify2FA = async (tfaToken, code) => {
    const response = await api.post('/auth/2fa/verify/', {
      temp_token: tfaToken,
      code
    });

    const { access_token, refresh_token, user: userData } = response.data;

    sessionStorage.setItem('access_token', access_token);
    // 2FA completion is a login too -- persist refresh alongside, same as login().
    if (refresh_token) sessionStorage.setItem('refresh_token', refresh_token);
    setUser(userData);
    resumeOfflinePunches(userData);

    return userData;
  };

  // Phase 6 — raw Google OAuth (NOT Emergent-managed)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = async () => {
    try {
      const redirectUri = window.location.origin + '/auth/google';
      const r = await api.get(`/auth/google/start/?redirect_uri=${encodeURIComponent(redirectUri)}`);
      if (r.data?.configured === false) {
        toast.error('Google sign-in is not configured. Contact your administrator.');
        return;
      }
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error('Could not start Google sign-in');
    }
  };

  const loginWithMicrosoft = async () => {
    try {
      const res = await api.get(`/auth/microsoft/auth-url/?origin=${encodeURIComponent(window.location.origin)}`);
      const data = res.data;
      if (!data.configured) {
        toast.error('Contact your administrator to setup Entra (Microsoft login is not configured).');
        return;
      }
      // Store state for CSRF validation in callback
      sessionStorage.setItem('microsoft_oauth_state', data.state || '');
      window.location.href = data.auth_url;
    } catch {
      toast.error('Unable to connect to Microsoft login. Please try again.');
    }
  };

  const processGoogleSession = async (sessionId) => {
    const response = await api.post('/auth/google/session/', { session_id: sessionId });
    const { session_token, user: userData } = response.data;

    localStorage.setItem('session_token', session_token);
    setUser(userData);
    resumeOfflinePunches(userData);

    return userData;
  };

  const processMicrosoftSession = async (accessToken) => {
    const response = await api.post('/auth/microsoft/session/', { access_token: accessToken });
    const { session_token, user: userData } = response.data;

    localStorage.setItem('session_token', session_token);
    setUser(userData);
    resumeOfflinePunches(userData);

    return userData;
  };

  const logout = async () => {
    const leaving = user;
    try {
      await api.post('/auth/logout/');
    } catch (error) {
      // Ignore logout errors
    } finally {
      try {
        const { clearPunchQueueForUser } = await import('../lib/offlinePunchQueue');
        await clearPunchQueueForUser(leaving?.id);
      } catch (error) {
        // The queue is best-effort. Logout still clears the session.
      }
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('session_token');
      sessionStorage.removeItem('impersonation_data');
      setUser(null);
      setIsImpersonating(false);
      setOriginalAdmin(null);
    }
  };

  const register = async (data) => {
    const response = await api.post('/auth/register/', data);
    const { access_token, refresh_token, user: userData } = response.data;

    sessionStorage.setItem('access_token', access_token);
    if (refresh_token) sessionStorage.setItem('refresh_token', refresh_token);
    setUser(userData);
    resumeOfflinePunches(userData);

    return userData;
  };

  // Impersonation functions
  const startImpersonation = async (targetUserId) => {
    const response = await api.post('/auth/impersonate/start/', { user_id: targetUserId });
    const { access_token, user: userData, original_admin } = response.data;

    const returnTo = `${window.location.pathname}${window.location.hash}`;

    sessionStorage.setItem('access_token', access_token);
    sessionStorage.setItem('impersonation_data', JSON.stringify({ original_admin, return_to: returnTo }));

    setUser(userData);
    setIsImpersonating(true);
    setOriginalAdmin(original_admin);

    return userData;
  };

  const stopImpersonation = async () => {
    if (!originalAdmin?.id) {
      throw new Error('No impersonation session to stop');
    }

    // Check if this is tenant impersonation (has impersonated_user with tenant info)
    const impersonationData = sessionStorage.getItem('impersonation_data');
    const parsedImpersonationData = impersonationData ? JSON.parse(impersonationData) : null;
    const isTenantImpersonation = parsedImpersonationData?.impersonated_user?.tenant;
    const returnTo = parsedImpersonationData?.return_to || '/admin/rbac#matrix';

    let response;
    if (isTenantImpersonation) {
      // Use tenant stop endpoint
      response = await api.post('/auth/impersonate/tenant-stop/', {
        admin_id: originalAdmin.id,
        admin_schema: 'k12erp'  // Super admins are in k12erp schema
      });
    } else {
      // admin_id is no longer sent — backend reads original_admin_id from the JWT claim
      response = await api.post('/auth/impersonate/stop/', {});
    }

    const { access_token, user: userData } = response.data;

    sessionStorage.setItem('access_token', access_token);
    sessionStorage.removeItem('impersonation_data');

    setUser(userData);
    setIsImpersonating(false);
    setOriginalAdmin(null);

    return { user: userData, returnTo };
  };

  const setup2FA = async (phone) => {
    const response = await api.post('/auth/2fa/setup/', { phone });
    return response.data;
  };

  const verify2FASetup = async (code) => {
    const response = await api.post('/auth/2fa/setup/verify/', { code });
    if (response.data.tfa_enabled) {
      setUser(prev => ({ ...prev, tfa_enabled: true }));
    }
    return response.data;
  };

  const disable2FA = async (currentPassword) => {
    const response = await api.post('/auth/2fa/disable/', { current_password: currentPassword });
    if (!response.data.tfa_enabled) {
      setUser(prev => ({ ...prev, tfa_enabled: false }));
    }
    return response.data;
  };

  const value = {
    user,
    setUser,
    loading,
    isImpersonating,
    originalAdmin,
    login,
    verify2FA,
    completeLoginFromResponse,
    loginWithGoogle,
    loginWithMicrosoft,
    processGoogleSession,
    processMicrosoftSession,
    logout,
    register,
    checkAuth,
    startImpersonation,
    stopImpersonation,
    setup2FA,
    verify2FASetup,
    disable2FA,
    isAuthenticated: !!user,
    isSuperAdmin: user?.is_super_admin || false,
    token: sessionStorage.getItem('access_token'),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
